"use client";

import React, { useState } from "react";
import type { CustomerLanguage } from "@/lib/customer-language";
import type { NaamPersonReadiness } from "@/lib/api/matching-naam";
import { matchingCopy } from "../matching-copy";
import { NaamAksharPicker } from "./NaamAksharPicker";
import { cellFromOption, type NaamCell } from "./naam-cell";

/**
 * One person's naam input and, when the table cannot settle it, the question.
 *
 * The three states come straight from the server resolver — `resolved`,
 * `needs_choice`, `needs_input` — and the UI never adds a fourth or collapses
 * two. A typed NAME can only ever reach `needs_choice`, by construction on the
 * backend: the everyday name is not evidence of the janma naam, so no amount of
 * client confidence may turn it into a score.
 */
export function NaamPersonField({
  heading,
  language,
  value,
  isName,
  readiness,
  confirmed,
  onValueChange,
  onIsNameChange,
  onConfirm,
  onClear,
}: {
  heading: string;
  language: CustomerLanguage;
  value: string;
  isName: boolean;
  readiness: NaamPersonReadiness | null;
  confirmed: NaamCell | null;
  onValueChange: (value: string) => void;
  onIsNameChange: (isName: boolean) => void;
  onConfirm: (cell: NaamCell) => void;
  onClear: () => void;
}) {
  const copy = matchingCopy(language);
  const [pickerOpen, setPickerOpen] = useState(false);

  if (confirmed) {
    return (
      <div className="card-sacred flex items-center justify-between p-4">
        <div>
          <p className="text-eyebrow font-semibold uppercase tracking-wider text-ish-text-faint">
            {heading} &middot; {copy.naamConfirmed}
          </p>
          <p className="text-body font-bold text-ish-dark">
            {confirmed.syllable} &middot; {confirmed.nakshatra_label}
          </p>
          <p className="text-caption text-ish-text-muted">
            {copy.naamPada} {confirmed.pada}
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-caption font-semibold text-intent-brand"
        >
          {copy.naamChange}
        </button>
      </div>
    );
  }

  return (
    <div className="card-sacred space-y-3 p-4">
      <p className="text-eyebrow font-semibold uppercase tracking-wider text-ish-text-faint">
        {heading}
      </p>
      <label className="block text-body-small text-ish-text-muted" htmlFor={`naam-${heading}`}>
        {isName ? copy.naamNameLabel : copy.naamAksharLabel}
      </label>
      <input
        id={`naam-${heading}`}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        className="w-full rounded-lg border border-ish-border px-3 py-2 text-body text-ish-dark"
        autoComplete="off"
      />
      <div className="flex gap-2">
        <ModeChip
          label={copy.naamIsNameOption}
          active={isName}
          onClick={() => onIsNameChange(true)}
        />
        <ModeChip
          label={copy.naamIsAksharOption}
          active={!isName}
          onClick={() => onIsNameChange(false)}
        />
      </div>

      {readiness?.state === "needs_choice" && (
        <div>
          <p className="text-body font-semibold text-ish-dark">{copy.naamChooseHeading}</p>
          <p className="mb-2 text-body-small text-ish-text-muted">{copy.naamChooseBody}</p>
          <div className="flex flex-wrap gap-2">
            {readiness.options.map((option) => (
              <button
                key={`${option.moon_nakshatra}-${option.pada}`}
                type="button"
                onClick={() => onConfirm(cellFromOption(option, language))}
                className="rounded-md border border-ish-border px-3 py-2 text-left text-body text-ish-dark hover:border-ish-accent"
              >
                <span className="font-semibold">{option.syllable_hi || option.syllable_en}</span>
                <span className="ml-2">{cellFromOption(option, language).nakshatra_label}</span>
                <span className="ml-1.5 text-eyebrow text-ish-text-faint">
                  {copy.naamPada} {option.pada}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {readiness?.state === "needs_input" && (
        <div>
          <p className="text-body font-semibold text-ish-dark">{copy.naamNotFoundHeading}</p>
          <p className="text-body-small text-ish-text-muted">{copy.naamNotFoundBody}</p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setPickerOpen((open) => !open)}
        className="text-caption font-semibold text-intent-brand"
      >
        {pickerOpen ? copy.naamPickerClose : copy.naamPickerOpen}
      </button>
      {pickerOpen && <NaamAksharPicker language={language} onPick={onConfirm} />}
    </div>
  );
}

function ModeChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-caption ${
        active
          ? "border-ish-accent font-semibold text-intent-brand"
          : "border-ish-border text-ish-text-muted"
      }`}
    >
      {label}
    </button>
  );
}
